import { useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  Database,
  Download,
  Upload,
  FileJson,
  FileSpreadsheet,
  CheckCircle2,
} from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Customer } from '@/data/types';

export function ImportExport() {
  const t = useT();
  const customers = useApp(s => s.customers);
  const selected = useApp(s => s.selectedCustomerIds);
  const replaceAll = useApp(s => s.replaceAll);
  const toast = useApp(s => s.toast);
  const fileInputJson = useRef<HTMLInputElement>(null);
  const fileInputXlsx = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState<string | null>(null);

  const exportSelected = () => {
    const ids = Array.from(selected);
    const subset = ids.length
      ? customers.filter(c => ids.includes(c.id))
      : customers;
    const blob = new Blob([JSON.stringify(subset, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fire-track-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast(`Exported ${subset.length} customers`);
  };

  const importJson = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string);
        if (!Array.isArray(data)) throw new Error('not array');
        if (!confirm(t('io.importConfirm'))) return;
        replaceAll({ customers: data as Customer[] });
        toast(`${t('io.importSuccess')} — ${data.length} customers`);
      } catch {
        toast(t('io.importError'));
      }
    };
    reader.readAsText(file);
  };

  const handleJsonDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(null);
    const file = e.dataTransfer.files[0];
    if (file) importJson(file);
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) importJson(file);
    e.target.value = '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Database className="h-6 w-6 text-primary" /> {t('io.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('io.subtitle')}</p>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Download className="h-4 w-4" /> {t('overview.export')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {selected.size > 0
                ? `${selected.size} selected`
                : `All ${customers.length}`}{' '}
              {/* i18n-ignore */}
              customers as JSON
              {/* i18n-ignore */}
            </p>
            <Button onClick={exportSelected} className="w-full">
              <Download className="h-4 w-4" />
              {t('io.downloadJson')}
            </Button>
          </CardContent>
        </Card>

        <Card data-focus="json-import">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <FileJson className="h-4 w-4" /> {t('io.importJson')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => fileInputJson.current?.click()}
              onDragOver={e => {
                e.preventDefault();
                setDrag('json');
              }}
              onDragLeave={() => setDrag(null)}
              onDrop={handleJsonDrop}
              className={`w-full border-2 border-dashed rounded-md p-6 text-center text-xs transition ${
                drag === 'json'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <div>{t('io.dropJson')}</div>
            </button>
            <input
              ref={fileInputJson}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleJsonChange}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" /> {t('io.importJson')}
              {/* reuse label — Excel label */}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => toast(t('io.excelSoon'))}
              onDragOver={e => {
                e.preventDefault();
                setDrag('xlsx');
              }}
              onDragLeave={() => setDrag(null)}
              onDrop={e => {
                e.preventDefault();
                setDrag(null);
                toast(t('io.excelSoon'));
              }}
              className={`w-full border-2 border-dashed rounded-md p-6 text-center text-xs transition ${
                drag === 'xlsx'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <div>{t('io.excelSoon')}</div>
            </button>
            <input
              ref={fileInputXlsx}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={() => toast(t('io.excelSoon'))}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('io.dataSummary')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 text-sm">
            {[
              { k: 'Customers', v: customers.length },
              {
                k: 'Extinguishers',
                v: customers.reduce((s, c) => s + c.extinguishers.length, 0),
              },
              {
                k: 'Hoses',
                v: customers.reduce((s, c) => s + c.hoses.length, 0),
              },
              {
                k: 'Archives',
                v: customers.reduce((s, c) => s + c.archives.length, 0),
              },
            ].map(it => (
              <div key={it.k} className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">{it.k}</div>
                {/* i18n-ignore */}
                <div className="text-2xl font-bold flex items-center gap-2">
                  {it.v} <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
